/**
 * Registro de seções "sujas" da página de domínio — infra pura da sticky save
 * bar (03c). Task 5 do plano do admin hub.
 *
 * Contrato duro (Global Constraint do plano): a barra NUNCA conhece
 * endpoint, query key ou mutation nenhuma. Cada seção chama
 * `useSectionSave(reg)` passando o próprio `save`/`discard`/`undo` — que usam
 * a mutation EXISTENTE da seção. Este módulo só agrega e chama esses
 * callbacks; "1 caminho de escrita por dado" continua morando na seção.
 *
 * Não é store zustand global: um store global vazaria registros entre
 * domínios (a página de outro domínio veria seções que não são dela). O
 * registro é escopado a UMA árvore de `DomainSaveProvider` — React context +
 * estado, não `useSyncExternalStore` (0 precedente no app — medido em
 * context.md 22/Ago/2026; a opção "estado + versão" do próprio plano é a que
 * este módulo usa).
 *
 * Cuidado de implementação — hazard de loop de render: uma seção pode passar
 * uma NOVA identidade de objeto `SectionSaveRegistration` a cada render (o
 * caso comum é um objeto literal recriado inline). Se o registro decidisse
 * invalidar (re-renderizar quem lê `sections`) toda vez que a IDENTIDADE do
 * objeto muda, o resultado é loop: registro muda → contexto muda → seção
 * (consumidora do contexto via `useSectionSave`) re-renderiza → recria o
 * objeto → registro muda de novo → ... Por isso `register()` sempre atualiza
 * os closures internamente (save/discard/undo ficam frescos a cada chamada),
 * mas só dispara `setVersion` — o único gatilho de re-render de quem lê
 * `sections` — quando o SNAPSHOT apresentável de fato mudou: a lista de
 * `changedLabels` ou a presença de `undo`. `useSectionSave` chama
 * `register()` em TODO render (sem array de deps por identidade), de
 * propósito — o filtro fica dentro de `register()`, não no array de deps do
 * efeito. `StickySaveBar.spec.tsx` tem um teste dedicado que falsifica isto:
 * uma seção fake que recria a registration a cada render, provando que a
 * contagem de renders não explode.
 *
 * Fix round 1 (Finding 1, CRITICAL) — `sections` é um SNAPSHOT de exibição,
 * gateado por `version`, e por isso pode ficar apontando para um closure de
 * `save`/`discard` VELHO: se a seção reregistra com o MESMO `changedLabels`
 * (ex.: 2ª tecla no mesmo campo — o `changedLabels` não muda, só o valor que
 * o closure de `save` fecha), `sameSnapshot` é `true`, `version` não bumpa, e
 * `sections` (o array memoizado) continua apontando pro closure da 1ª tecla.
 * Um consumidor que executasse `save()`/`discard()` a partir de `sections`
 * escreveria o valor ANTIGO enquanto a tela mostra o NOVO — corrupção
 * silenciosa. `sections` continua existindo (é o que a barra usa pra
 * RENDERIZAR contagem/labels — está sempre correto para isso, porque
 * qualquer mudança visível de label troca `sameSnapshot` para `false`), mas
 * quem for EXECUTAR save/discard/undo precisa ler o registro ao vivo no
 * momento do clique. `getSections()` existe exatamente pra isso: não é
 * memoizado, sempre dereferencia `registryRef.current` na hora da chamada.
 * `StickySaveBar.spec.tsx` tem o teste de falsificação dedicado (reregistra
 * com o mesmo changedLabels e um `save` diferente; afirma que o clique em
 * Salvar chama o `save` mais recente).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { ReactNode } from 'react';

export interface SectionSaveRegistration {
  readonly sectionId: string;
  /** Labels dos campos alterados — a barra e o toast NOMEIAM o que mudou (03c). */
  readonly changedLabels: readonly string[];
  readonly save: () => Promise<void>;
  readonly discard: () => void;
  /** Desfazer da sessão: re-aplica os valores anteriores pela MESMA mutation. */
  readonly undo?: () => Promise<void>;
}

interface RegistryEntry {
  current: SectionSaveRegistration;
}

interface SaveBarContextValue {
  register: (reg: SectionSaveRegistration) => void;
  unregister: (sectionId: string) => void;
  /** Snapshot memoizado — só para EXIBIÇÃO (contagem/labels). Ver docblock do módulo. */
  sections: readonly SectionSaveRegistration[];
  /** Leitura ao vivo, sempre fresca — para EXECUTAR save/discard/undo. */
  getSections: () => readonly SectionSaveRegistration[];
}

const SaveBarContext = createContext<SaveBarContextValue | null>(null);

/** Compara só o que é apresentável — nunca a identidade das funções. */
function sameSnapshot(a: SectionSaveRegistration, b: SectionSaveRegistration): boolean {
  return (
    a.changedLabels.length === b.changedLabels.length &&
    a.changedLabels.every((label, i) => label === b.changedLabels[i]) &&
    (a.undo === undefined) === (b.undo === undefined)
  );
}

function readLive(registry: Map<string, RegistryEntry>): readonly SectionSaveRegistration[] {
  return Array.from(registry.values()).map((e) => e.current);
}

/**
 * Envolve o conteúdo de UMA `DomainPage`. Cada domínio monta o seu — o
 * registro nunca atravessa domínios.
 */
export function DomainSaveProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const registryRef = useRef(new Map<string, RegistryEntry>());
  const [version, setVersion] = useState(0);

  // Deps vazias de propósito: o corpo só toca `registryRef.current` (ref
  // estável) e `setVersion` (setter estável do useState) — nunca props/state
  // que mudam, então a identidade de `register`/`unregister` fica estável
  // para sempre, o que mantém `value` (useMemo abaixo) estável também.
  const register = useCallback((reg: SectionSaveRegistration) => {
    const existing = registryRef.current.get(reg.sectionId);
    if (existing === undefined) {
      registryRef.current.set(reg.sectionId, { current: reg });
      setVersion((v) => v + 1);
      return;
    }
    const changed = !sameSnapshot(existing.current, reg);
    // Fresco sempre — o closure de save/discard/undo não pode ficar velho,
    // mesmo quando o snapshot apresentável não mudou (ex.: a seção capturou
    // um novo valor de campo que ainda resulta no mesmo label alterado).
    // É exatamente esse caso que `getSections()` existe para não deixar
    // vazar pra quem EXECUTA (ver docblock do módulo, Finding 1).
    existing.current = reg;
    if (changed) setVersion((v) => v + 1);
  }, []);

  const unregister = useCallback((sectionId: string) => {
    if (registryRef.current.delete(sectionId)) {
      setVersion((v) => v + 1);
    }
  }, []);

  // `version` é o único sinal de invalidação deliberado (o callback não lê
  // `version` — ele lê `registryRef`, uma ref de identidade estável que
  // exhaustive-deps não exige no array). O eslint do app acusa isso como
  // "dependência desnecessária" (warning, não erro — não quebra `pnpm lint`
  // nem o pre-commit). NÃO suprimir com `eslint-disable-next-line
  // react-hooks/exhaustive-deps`: o eslint.config.js da RAIZ (o que o hook
  // de pre-commit roda) não carrega o plugin `react-hooks`, e nessas
  // condições um disable-comment citando uma regra que o config não conhece
  // vira ERRO ("Definition for rule ... was not found") — medido ao
  // commitar esta task. Warning documentado > erro de commit.
  const sections = useMemo<readonly SectionSaveRegistration[]>(
    () => readLive(registryRef.current),
    [version],
  );

  // Nunca memoizado — cada chamada dereferencia `registryRef.current` na
  // hora. Identidade estável (deps vazias, só toca a ref) para não invalidar
  // `value` à toa.
  const getSections = useCallback((): readonly SectionSaveRegistration[] => {
    return readLive(registryRef.current);
  }, []);

  const value = useMemo<SaveBarContextValue>(
    () => ({ register, unregister, sections, getSections }),
    [register, unregister, sections, getSections],
  );

  return <SaveBarContext.Provider value={value}>{children}</SaveBarContext.Provider>;
}

function useSaveBarContextOrThrow(): SaveBarContextValue {
  const ctx = useContext(SaveBarContext);
  if (ctx === null) {
    throw new Error(
      'useSectionSave/useSaveBarSections precisa estar dentro de um DomainSaveProvider',
    );
  }
  return ctx;
}

/**
 * Chamado pela seção com sua própria registration, ou `null` quando não há
 * alteração pendente (a seção some da barra). Registra no mount/update,
 * desregistra no unmount — e também quando `reg` vira `null` num update (a
 * seção ficou limpa de novo sem desmontar).
 */
export function useSectionSave(reg: SectionSaveRegistration | null): void {
  const ctx = useSaveBarContextOrThrow();
  // Destructure: `register`/`unregister` são `useCallback(fn, [])` no
  // provider — a MESMA referência de função para toda a vida do provider,
  // não importa quantas vezes `ctx` (o objeto) mude de identidade por causa
  // de `sections`. Por isso dá pra usá-los honestamente num array de deps
  // (fix round 1, Finding 3) sem precisar de ref auxiliar nenhuma.
  const { register, unregister } = ctx;
  const registeredIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (reg !== null) {
      register(reg);
      registeredIdRef.current = reg.sectionId;
    } else if (registeredIdRef.current !== null) {
      unregister(registeredIdRef.current);
      registeredIdRef.current = null;
    }
    // Sem array de deps, de propósito (ver docblock do módulo): a seção pode
    // recriar `reg` a cada render, e o filtro de identidade mora DENTRO de
    // `register()` (sameSnapshot), não aqui. Rodar em todo render é barato
    // (uma escrita de Map) e é a única forma de manter os closures de
    // save/discard/undo sempre frescos sem reintroduzir o hazard de loop de
    // um array de deps baseado em identidade de objeto.
  });

  useEffect(() => {
    return () => {
      if (registeredIdRef.current !== null) {
        unregister(registeredIdRef.current);
        registeredIdRef.current = null;
      }
    };
    // Deps honestas: `unregister` é a única variável externa que o corpo
    // lê, e ela é estável (useCallback([]) no provider) — então isto roda
    // cleanup só no desmonte de verdade, exatamente como antes, sem
    // precisar de ref auxiliar nem de disable-comment nenhum.
  }, [unregister]);
}

/** Lido pela `StickySaveBar` para RENDERIZAR (contagem/labels) — snapshot memoizado. */
export function useSaveBarSections(): readonly SectionSaveRegistration[] {
  return useSaveBarContextOrThrow().sections;
}

/** Lido pela `StickySaveBar` para EXECUTAR (save/discard) — sempre fresco. */
export function useSaveBarLiveSections(): () => readonly SectionSaveRegistration[] {
  return useSaveBarContextOrThrow().getSections;
}
